/**
 * sqlite3mt manifest operations bridge — §104 public manifest v2
 *
 * This module provides the sqlite3mt-facing manifest operations that bridge
 * between the protocol-level Root/PageHash types and the public manifest v2
 * tree format defined in §104 §7 (sqlite3mt-manifest-v2 internal/leaf nodes).
 *
 * The critical operations here are:
 * - normalizeSparseOverlay: prune overlay entries that match the base root
 * - materializeRoot: create a new root from base + overlay using v2 manifest tree
 * - diffConcreteRoots: find pages that differ between two roots
 * - collectUploadSet: find blobs that need uploading
 * - getDbPageHash / getWorkspacePageHash: combined overlay+base lookups
 *
 * All push (§14), reconcile (§15), and compaction (§18) flows depend on
 * these operations. The RPC bridge (§97) connects them to the transport layer.
 *
 * Per §5.2 and §7: manifest objects are PUBLIC and server-readable.
 * They use sqlite3mt-manifest-v2 format with explicit internal/leaf node kinds.
 * Manifest node identity = SHA-256(canonicalUtf8(node)).
 */

import type { Root, RootId, PublicHead, PageHash, Pgno } from './types'
import {
    computePublicRootId,
    createPublicRootDescriptor,
    materializePublicManifestTree,
    materializePublicManifestIncremental,
    resolvePageObjectId,
    diffManifestTreesWithPageIndices,
    diffManifestTrees,
    type Sqlite3mtManifestV2Node,
    type MaterializedManifestTree,
} from './public-manifest'

// ── §12 Manifest page-hash lookup ─────────────────────────────

/**
 * Result of a page-hash lookup from a concrete root.
 *
 * null means the page is beyond the root's pageCount or is sparse
 * (no content at that position in the radix tree).
 */
export type PageHashResult = PageHash | null

// ── §104 v2 Manifest node cache ─────────────────────────────────

/**
 * In-memory cache for manifest nodes (Sqlite3mtManifestV2Node objects).
 *
 * The cache avoids redundant loads when the same manifest node is
 * accessed multiple times during a single push/reconcile operation.
 * Entries are keyed by their manifestObjectId (SHA-256 of canonical bytes).
 *
 * This is populated by the runtime from:
 * 1. stagedManifestNodes (pending writes)
 * 2. durable.getManifestNode(hash)
 * 3. rpcGetManifestNode(hash)
 */
export interface ManifestNodeCache {
    get(hash: string): Sqlite3mtManifestV2Node | undefined
    set(hash: string, node: Sqlite3mtManifestV2Node): void
    has(hash: string): boolean
}

// ── §104 Full manifest operations interface ──────────────────────────

/**
 * Complete manifest operations interface for sqlite3mt v2.
 *
 * Extends the partial ManifestOperations from types.ts with all
 * operations needed by push (§14), reconcile (§15), and compaction (§18).
 *
 * The runtime provides a concrete implementation backed by:
 * - ManifestNodeCache (in-memory)
 * - DurableLocalBlobStore (durable)
 * - RPC bridge (§97 transport)
 *
 * Per §104: manifest nodes use sqlite3mt-manifest-v2 format.
 */
export interface FullManifestOperations {
    /** §12: Get page hash from a concrete root. null if pgno > pageCount. */
    getPageHashFromConcreteRoot(root: Root, pgno: Pgno): Promise<PageHash | null>

    /** §12: Get raw page bytes by content hash. */
    getPageBytesByHash(hash: PageHash): Promise<Uint8Array | null>

    /** §12: Compute the logical sqlite3mt page hash for raw page bytes. */
    hashPage(data: Uint8Array): PageHash

    /** §12: Get page bytes for a specific pgno from a concrete root. */
    getPageBytesFromConcreteRoot(root: Root, pgno: Pgno): Promise<Uint8Array | null>

    /** §12: Diff two concrete roots → set of changed page numbers. */
    diffConcreteRoots(a: Root, b: Root): Promise<Set<Pgno>>

    /**
     * §12: Normalize a sparse overlay against a base root.
     *
     * Removes entries from the overlay where the overlay hash matches
     * the base root's hash for that page number. Also prunes entries
     * beyond targetPageCount.
     *
     * Returns the normalized overlay (new Map, input not mutated).
     */
    normalizeSparseOverlay(
        baseRoot: Root,
        overlay: ReadonlyMap<Pgno, PageHash>,
        targetPageCount: number
    ): Promise<Map<Pgno, PageHash>>

    /**
     * §12: Materialize a new root from base + overlay.
     *
     * Creates new manifest nodes for the changed paths in the radix tree,
     * uploads new blobs, and returns the new root descriptor plus
     * all objects that need to be stored.
     */
    materializeRoot(
        baseRoot: Root,
        overlay: ReadonlyMap<Pgno, Uint8Array>,
        targetPageCount: number,
        parentPublicRoot: PublicHead | null
    ): Promise<MaterializeResult>

    /**
     * §12: Collect the upload set from base → target.
     *
     * Returns the hashes of page blobs and manifest nodes that need
     * to be uploaded to the remote store.
     */
    collectUploadSet(
        baseRoot: Root | null,
        targetRoot: Root
    ): Promise<UploadSet>
}

// ── §12 Materialize result ─────────────────────────────────────

/**
 * Result of materializeRoot — the new root plus objects to store.
 */
export interface MaterializeResult {
    root: Root
    /** All new/changed manifest+page objects to store durably and/or upload. */
    objects: Array<{ hash: string; bytes: Uint8Array }>
    /** Upload set for the newly materialized target root. */
    uploadSet: UploadSet
}

/**
 * Set of hashes that need uploading from base → target.
 */
export interface UploadSet {
    /** Page blob hashes that need uploading. */
    pageBlobHashes: Set<string>
    /** Manifest node hashes that need uploading. */
    manifestNodeHashes: Set<string>
}

// ── §12 View-level hash lookups ────────────────────────────────

/**
 * §12: Get the effective page hash for DB-view at a given pgno.
 *
 * Checks dbOverlay first, then falls back to the concrete base root.
 * Returns null if pgno > dbPageCount.
 */
export async function getDbPageHash(
    pgno: Pgno,
    dbOverlay: ReadonlyMap<Pgno, PageHash>,
    dbPageCount: number,
    dbBaseRoot: Root,
    ops: Pick<FullManifestOperations, 'getPageHashFromConcreteRoot'>
): Promise<PageHash | null> {
    if (pgno > dbPageCount) return null
    const overlayHash = dbOverlay.get(pgno)
    if (overlayHash != null) return overlayHash
    return ops.getPageHashFromConcreteRoot(dbBaseRoot, pgno)
}

/**
 * §12: Get the effective page hash for workspace-view at a given pgno.
 *
 * Checks workspaceOverlay first, then falls back to the concrete
 * workspaceBaseHead.root. Returns null if pgno > workspacePageCount.
 */
export async function getWorkspacePageHash(
    pgno: Pgno,
    workspaceOverlay: ReadonlyMap<Pgno, PageHash>,
    workspacePageCount: number,
    workspaceBaseHead: PublicHead,
    ops: Pick<FullManifestOperations, 'getPageHashFromConcreteRoot'>
): Promise<PageHash | null> {
    if (pgno > workspacePageCount) return null
    const overlayHash = workspaceOverlay.get(pgno)
    if (overlayHash != null) return overlayHash
    return ops.getPageHashFromConcreteRoot(workspaceBaseHead.root, pgno)
}

// ── §12 Overlay normalization ──────────────────────────────────

/**
 * §12: Prune overlay entries that are identical to the base root.
 *
 * For each entry in the overlay, resolves the base root's hash for
 * that page. If they match, the entry is removed from the result.
 * Entries beyond targetPageCount are also pruned.
 *
 * This is O(|overlay|) and is the core of push-delta normalization:
 * a push should only upload pages that actually changed.
 */
export async function normalizeSparseOverlay(
    baseRoot: Root,
    overlay: ReadonlyMap<Pgno, PageHash>,
    targetPageCount: number,
    ops: Pick<FullManifestOperations, 'getPageHashFromConcreteRoot'>
): Promise<Map<Pgno, PageHash>> {
    const result = new Map<Pgno, PageHash>()

    for (const [pgno, hash] of overlay) {
        if (pgno > targetPageCount) continue

        const baseHash = await ops.getPageHashFromConcreteRoot(baseRoot, pgno as Pgno)
        if (hash !== baseHash) {
            result.set(pgno, hash)
        }
    }

    return result
}

// ── §97 RPC bridge interface ───────────────────────────────────

/**
 * §97: RPC bridge to SecureBlob transport layer.
 *
 * These 6 primitives are the complete interface between sqlite3mt
 * and the SecureBlob v3.1 transport. No other transport calls are
 * needed for push, reconcile, or compaction.
 *
 * The main thread provides this via postMessage routing; the
 * Control Worker calls through this interface.
 */
export interface SecureBlobRpcBridge {
    /** §97: Get current public head with version. */
    rpcGetHeadVersioned(): Promise<{ root: Root; version: bigint }>

    /** Optional debug getter used to expose transport progress in diagnostics. */
    getDebugStage?(): string | null
    getDebugDetails?(): unknown

    /**
     * §97: CAS-publish a new head.
     *
     * On success: { ok: true }
     * On mismatch: { ok: false, currentRoot, currentVersion }
     */
    rpcPublishIfCurrent(
        expectedRoot: Root,
        expectedVersion: bigint,
        nextRoot: Root,
        publishPrefetchHints?: string[]
    ): Promise<PublishResult>

    /** §97: Get a blob by content hash. Returns null if not found. */
    rpcGetBlob(hash: string): Promise<Uint8Array | null>

    /**
     * §97: Get blobs by content hash. Demand-driven callers pass only hashes
     * that are already needed; implementations should use the remote batch
     * object endpoint when available and return only found hashes.
     */
    rpcGetBlobs?(hashes: string[]): Promise<Map<string, Uint8Array>>

    /** §97: Upload missing blobs. The loader provides bytes for each hash. */
    rpcPutMissingBlobs(
        hashes: Iterable<string>,
        loader: (hash: string) => Uint8Array | Promise<Uint8Array>
    ): Promise<void>

    /** §97: Get a manifest node by hash. Same CAS namespace as blobs. */
    rpcGetManifestNode(hash: string): Promise<Uint8Array | null>

    /**
     * §97: Get manifest nodes by hash. Manifest nodes are public CAS objects,
     * so implementations should use the same remote batch endpoint as page
     * blobs when several adjacent manifest nodes are needed.
     */
    rpcGetManifestNodes?(hashes: string[]): Promise<Map<string, Uint8Array>>

    /** §97: Upload missing manifest nodes. Same CAS namespace as blobs. */
    rpcPutMissingManifestNodes(
        hashes: Iterable<string>,
        loader: (hash: string) => Uint8Array | Promise<Uint8Array>
    ): Promise<void>
}

/** Result of a CAS-publish attempt. */
export type PublishResult =
    | { ok: true }
    | { ok: false; currentRoot: Root; currentVersion: bigint }

// ── §104 Factory deps ──────────────────────────────────────────

/**
 * Dependencies for buildManifestOperations factory.
 *
 * The factory creates a concrete FullManifestOperations implementation
 * backed by these deps. The runtime wires the deps to:
 * - ManifestNodeCache + DurableLocalBlobStore + rpcGetManifestNode (for loading)
 * - SecureBlobRpcBridge (for remote transport)
 * - Logical hash deps (sha256/hashPage for materializeRoot/root identity)
 *
 * Per §104: loadManifestNode returns parsed v2 manifest nodes.
 * Manifest nodes are stored as cleartext (public per §5.2).
 */
export interface ManifestOpsDeps {
    /** Load a manifest node by manifestObjectId. Returns parsed v2 node or null. */
    loadManifestNode(hash: string): Promise<Sqlite3mtManifestV2Node | null>

    /** Load raw manifest node bytes by manifestObjectId. Returns null if not found. */
    loadManifestNodeBytes?(hash: string): Promise<Uint8Array | null>

    /** Load a page blob by content hash. Returns null if not found. */
    loadPageBlob(hash: string): Promise<Uint8Array | null>

    /** Compute SHA-256 hash, returned as hex string. */
    sha256(data: Uint8Array): Promise<string>

    /** Compute the logical sqlite3mt page hash for raw page bytes. */
    hashPage(data: Uint8Array): PageHash

    /** Check if object exists in local store (for write-path dedup). */
    hasObject(hash: string): Promise<boolean>

    /** Block/page size in bytes. */
    blockSize: number
}

// ── §104 Concrete factory ──────────────────────────────────────

/**
 * Build a concrete FullManifestOperations from deps.
 *
 * This factory uses the §104 v2 public manifest tree (internal/leaf nodes)
 * instead of the old flat BlockManifestV3. Every push, reconcile, and
 * compaction flow uses the returned object for all manifest operations.
 *
 * Per §5.2: manifest nodes are public and server-readable.
 * Per §7: fixed-depth sparse radix tree with fanout=256, depth=4.
 */
export function buildManifestOperations(deps: ManifestOpsDeps): FullManifestOperations {
    const {
        loadManifestNode,
        loadManifestNodeBytes,
        loadPageBlob,
        sha256,
        hashPage,
        hasObject,
        blockSize,
    } = deps

    function assertConcreteManifestRoot(root: Root): string {
        if (root.meta.manifestRootId) {
            return root.meta.manifestRootId
        }
        if (root.meta.pageCount === 0) {
            throw new Error('EMPTY_MANIFEST_ROOT')
        }
        throw new Error(`INVALID_MANIFEST_ROOT: ${root.rootId}`)
    }

    /**
     * Load a v2 manifest node from either parsed cache or raw bytes.
     */
    async function loadV2Node(hash: string): Promise<Sqlite3mtManifestV2Node | null> {
        return loadManifestNode(hash)
    }

    // ── getPageHashFromConcreteRoot ──────────────────────────────

    async function getPageHashFromConcreteRoot(root: Root, pgno: Pgno): Promise<PageHash | null> {
        if ((pgno as number) > root.meta.pageCount) return null
        if (root.meta.pageCount === 0 || !root.meta.manifestRootId) return null

        // pgno is 1-based (SQLite convention); manifest uses 0-based index
        const pageIndex = (pgno as number) - 1

        const pageObjectId = await resolvePageObjectId(
            root.meta.manifestRootId,
            pageIndex,
            loadV2Node,
        )
        return pageObjectId as PageHash | null
    }

    // ── getPageBytesByHash ───────────────────────────────────────

    async function getPageBytesByHash(hash: PageHash): Promise<Uint8Array | null> {
        return loadPageBlob(hash as string)
    }

    function computePageHash(bytes: Uint8Array): PageHash {
        return hashPage(bytes)
    }

    // ── getPageBytesFromConcreteRoot ─────────────────────────────

    async function getPageBytesFromConcreteRoot(root: Root, pgno: Pgno): Promise<Uint8Array | null> {
        const hash = await getPageHashFromConcreteRoot(root, pgno)
        if (hash == null) return null
        return loadPageBlob(hash as string)
    }

    // ── diffConcreteRoots ────────────────────────────────────────

    async function diffConcreteRoots(a: Root, b: Root): Promise<Set<Pgno>> {
        if (a.meta.pageCount === 0 && b.meta.pageCount === 0) {
            return new Set<Pgno>()
        }

        const aId = a.meta.pageCount > 0 ? a.meta.manifestRootId : null
        const bId = b.meta.pageCount > 0 ? b.meta.manifestRootId : null

        if (aId === bId) return new Set<Pgno>()

        const diff = await diffManifestTreesWithPageIndices(
            aId || null,
            bId || null,
            loadV2Node,
        )

        // If we have asymmetric page counts, report all pages in the wider range
        const maxCount = Math.max(a.meta.pageCount, b.meta.pageCount)
        const minCount = Math.min(a.meta.pageCount, b.meta.pageCount)

        const changed = new Set<Pgno>()
        // Pages beyond the shorter root's pageCount are all changed
        for (let i = minCount; i < maxCount; i++) {
            changed.add((i + 1) as Pgno)
        }

        // Use the page-index-aware diff directly — O(changed × depth)
        // instead of the old O(N × depth) per-page resolution pass.
        for (const pageIndex of diff.changedPageIndices) {
            const pgno = (pageIndex + 1) as Pgno
            // Clamp to pages that exist in at least one root
            if ((pageIndex as number) < maxCount) {
                changed.add(pgno)
            }
        }

        return changed
    }

    // ── normalizeSparseOverlay ───────────────────────────────────

    async function _normalizeSparseOverlay(
        baseRoot: Root,
        overlay: ReadonlyMap<Pgno, PageHash>,
        targetPageCount: number,
    ): Promise<Map<Pgno, PageHash>> {
        const result = new Map<Pgno, PageHash>()

        for (const [pgno, hash] of overlay) {
            if ((pgno as number) > targetPageCount) continue

            const baseHash = await getPageHashFromConcreteRoot(baseRoot, pgno)
            if (hash !== baseHash) {
                result.set(pgno, hash)
            }
        }

        return result
    }

    // ── materializeRoot ──────────────────────────────────────────

    async function _materializeRoot(
        baseRoot: Root,
        overlay: ReadonlyMap<Pgno, Uint8Array>,
        targetPageCount: number,
        parentPublicHead: PublicHead | null,
    ): Promise<MaterializeResult> {
        const objects: Array<{ hash: string; bytes: Uint8Array }> = []
        const pageBlobHashes = new Set<string>()

        // Build 0-based page map: pgno (1-based) → pageObjectId
        const pageSlots = new Map<number, string>()
        for (const [pgno, pageData] of overlay) {
            const pageIndex = (pgno as number) - 1
            if (pageIndex < 0 || pageIndex >= targetPageCount) continue
            const hash = hashPage(pageData)
            pageSlots.set(pageIndex, hash)
            pageBlobHashes.add(hash)
            objects.push({ hash, bytes: pageData })
        }

        // Build v2 public manifest tree
        let tree: MaterializedManifestTree

        if (baseRoot != null && baseRoot.meta.manifestRootId && baseRoot.meta.pageCount > 0) {
            // Incremental: reuse unchanged subtrees from base
            tree = await materializePublicManifestIncremental(
                baseRoot.meta.manifestRootId,
                pageSlots,
                targetPageCount,
                loadV2Node,
                sha256,
                baseRoot.meta.pageCount,
            )
        } else {
            // Full materialization (first publish or empty base)
            tree = await materializePublicManifestTree(pageSlots, targetPageCount, sha256)
        }

        // Stage all manifest node bytes as objects to upload
        for (const [id, bytes] of tree.nodeBytes) {
            objects.push({ hash: id, bytes })
        }

        const manifestRootId = tree.manifestRootId ?? ''

        // Compute rootId via canonical descriptor
        const parentPublicRootId = parentPublicHead ? parentPublicHead.root.rootId as string : null
        const rootIdStr = await computePublicRootId(
            createPublicRootDescriptor({
                manifestRootId: targetPageCount === 0 ? null : manifestRootId,
                pageCount: targetPageCount,
                pageSize: blockSize,
                parentPublicRootId,
            }),
            sha256,
        )

        return {
            root: {
                rootId: rootIdStr as RootId,
                meta: {
                    manifestRootId,
                    pageCount: targetPageCount,
                    parentPublicRootId: parentPublicRootId as RootId | null,
                },
            },
            objects,
            uploadSet: {
                pageBlobHashes,
                manifestNodeHashes: new Set<string>(tree.nodes.keys()),
            },
        }
    }

    // ── collectUploadSet ─────────────────────────────────────────

    async function _collectUploadSet(
        baseRoot: Root | null,
        targetRoot: Root,
    ): Promise<UploadSet> {
        if (targetRoot.meta.pageCount === 0) {
            return { pageBlobHashes: new Set(), manifestNodeHashes: new Set() }
        }

        const targetManifestHash = assertConcreteManifestRoot(targetRoot)

        const pageBlobHashes = new Set<string>()
        const manifestNodeHashes = new Set<string>()

        if (baseRoot == null || baseRoot.meta.pageCount === 0 || !baseRoot.meta.manifestRootId) {
            // No base: everything in target is new
            // Walk target tree to collect all page and manifest object IDs
            const allReachable = await collectAllReachable(targetManifestHash, loadV2Node)
            for (const id of allReachable.pageObjectIds) pageBlobHashes.add(id)
            for (const id of allReachable.manifestObjectIds) manifestNodeHashes.add(id)
        } else {
            // Diff base → target
            const diff = await diffManifestTrees(
                baseRoot.meta.manifestRootId,
                targetManifestHash,
                loadV2Node,
            )

            for (const id of diff.addedPageObjectIds) pageBlobHashes.add(id)
            for (const id of diff.addedManifestObjectIds) manifestNodeHashes.add(id)
            // Also include the target root manifest node
            manifestNodeHashes.add(targetManifestHash)
        }

        return { pageBlobHashes, manifestNodeHashes }
    }

    // ── Return concrete FullManifestOperations ───────────────────

    return {
        getPageHashFromConcreteRoot,
        getPageBytesByHash,
        hashPage: computePageHash,
        getPageBytesFromConcreteRoot,
        diffConcreteRoots,
        normalizeSparseOverlay: _normalizeSparseOverlay,
        materializeRoot: _materializeRoot,
        collectUploadSet: _collectUploadSet,
    }
}

/**
 * Collect all reachable object IDs from a manifest tree root.
 */
async function collectAllReachable(
    rootId: string,
    loadNode: (id: string) => Promise<Sqlite3mtManifestV2Node | null>,
): Promise<{ manifestObjectIds: Set<string>; pageObjectIds: Set<string> }> {
    const manifestObjectIds = new Set<string>()
    const pageObjectIds = new Set<string>()

    async function walk(id: string): Promise<void> {
        if (manifestObjectIds.has(id)) return
        manifestObjectIds.add(id)

        const node = await loadNode(id)
        if (node == null) return

        if (node.kind === 'internal') {
            for (const child of node.children) {
                if (child != null) await walk(child)
            }
        } else if (node.kind === 'leaf') {
            for (const page of node.pages) {
                if (page != null) pageObjectIds.add(page)
            }
        }
    }

    await walk(rootId)
    return { manifestObjectIds, pageObjectIds }
}
